import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { tableConfig } from "../../../commons/table/tableConfig";
import { StyledResolucion } from "../../../styled/Prevencion";

const MAX_SUSTANCIAS_CHARS = 140;

const SustanciasCell = ({ sustanciaIds, allSustancias }) => {
  const [expanded, setExpanded] = useState(false);

  const sustanciaNames = (sustanciaIds || []).map((id) => {
    const sustanciaObj = allSustancias.find((s) => s?.id === id);
    if (sustanciaObj) {
      return (sustanciaObj.sustancia?.nombreAgente ?? "") + ' - ' + (sustanciaObj.sustancia?.descripcionAgente ?? "");
    }
    return `ID: ${id}`;
  });

  if (sustanciaNames.length === 0) return "Sin sustancias asociadas";

  const fullText = sustanciaNames.map((n) => `• ${n}`).join("\n");

  if (fullText.length <= MAX_SUSTANCIAS_CHARS || expanded) {
    return (
      <Box>
        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
          {fullText}
        </Typography>
        {expanded && fullText.length > MAX_SUSTANCIAS_CHARS && (
          <Link
            component="button"
            variant="caption"
            onClick={() => setExpanded(false)}
            sx={{ mt: 0.5 }}
          >
            Ver menos
          </Link>
        )}
      </Box>
    );
  }

  const truncated = fullText.substring(0, MAX_SUSTANCIAS_CHARS) + "...";
  return (
    <Box>
      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
        {truncated}
      </Typography>
      <Link
        component="button"
        variant="caption"
        onClick={() => setExpanded(true)}
        sx={{ mt: 0.5 }}
      >
        Ver más
      </Link>
    </Box>
  );
};

const PuestosAsociadosTable = ({ data, handleOpenFormularioPuesto, handleDelete, handleCancelResolucion, handleNext }) => {
  const sustancias = useSelector(
    (state) => state.resolucion.postResolucion.sustancias
  );

  const columns = useMemo(() => [
    {
      accessorKey: "nombrePuesto",
      header: "Puesto",
      filterVariant: 'text',
      minSize: 120,
      maxSize: 250,
      size: 150,
    },
    {
      accessorKey: "descripcionActividades",
      header: "Descripción",
      filterVariant: 'text',
      minSize: 150,
      maxSize: 350,
      size: 200,
    },
    {
      accessorKey: "sustancias",
      id: "sustancias",
      header: "Sustancias",
      minSize: 150,
      maxSize: 400,
      size: 250,
      Cell: ({ row }) => (
        <SustanciasCell
          sustanciaIds={row.original.sustancias}
          allSustancias={sustancias}
        />
      ),
    },
    {
      accessorKey: "informaRiesgos",
      header: "Inf. Rie.",
      filterVariant: 'select',
      filterSelectOptions: [
        { text: 'Si', value: true },
        { text: 'No', value: false }
      ],
      Cell: ({ cell }) => cell.getValue() ? "Sí" : "No",
      minSize: 80,
      maxSize: 120,
      size: 100,
    },
    {
      accessorKey: "poseeCapacitaciones",
      header: "Cap. Rie.",
      filterVariant: 'select',
      filterSelectOptions: [
        { text: 'Si', value: true },
        { text: 'No', value: false }
      ],
      Cell: ({ cell }) => cell.getValue() ? "Sí" : "No",
      minSize: 80,
      maxSize: 120,
      size: 100,
    },
    {
      accessorKey: "entregaProteccion",
      header: "Ent. Pro.",
      filterVariant: 'select',
      filterSelectOptions: [
        { text: 'Si', value: true },
        { text: 'No', value: false }
      ],
      Cell: ({ cell }) => cell.getValue() ? "Sí" : "No",
      minSize: 80,
      maxSize: 120,
      size: 100,
    },
    {
      accessorKey: "licenciasEspeciales",
      header: "Lic. Esp.",
      filterVariant: 'select',
      filterSelectOptions: [
        { text: 'Si', value: true },
        { text: 'No', value: false }
      ],
      Cell: ({ cell }) => cell.getValue() ? "Sí" : "No",
      minSize: 80,
      maxSize: 120,
      size: 100,
    }
  ], [sustancias]);

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data,
    enableRowSelection: true,
    enableRowActions: true,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Operaciones',
        size: 120,
        muiTableHeadCellProps: {
          sx: {
            ...tableConfig.muiTableHeadCellProps?.sx,
            justifyContent: "start"
          }
        }
      },
    },
    renderRowActions: ({ row }) => (
      <Tooltip title="Editar Puesto">
        <IconButton
          onClick={() => handleOpenFormularioPuesto({ ...row.original, index: row.index })}
          size="small"
          color="primary"
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <StyledResolucion.HeaderContainer>
        <StyledResolucion.TableTitle>
          Puestos Asociados a Sustancias
        </StyledResolucion.TableTitle>
      </StyledResolucion.HeaderContainer>
    ),
    renderBottomToolbarCustomActions: () => (
      <StyledResolucion.TableFooterButtonContainer>
        <Button
          color="primary"
          onClick={() => handleOpenFormularioPuesto(null)}
        >
          Agregar
        </Button>
      </StyledResolucion.TableFooterButtonContainer>
    ),
    renderToolbarAlertBannerContent: ({ groupedAlert, selectedAlert, table }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginLeft: 1,
        }}>
        {selectedAlert}
        <Tooltip title="Eliminar seleccionados">
          <IconButton
            onClick={() => {
              const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
              handleDelete(selectedRows);
              table.resetRowSelection();
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    enableFiltering: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    muiPaginationProps: {
      labelRowsPerPage: "Filas por página:",
      getItemAriaLabel: (type) => {
        if (type === 'first') return 'ir a la primera página';
        if (type === 'last') return 'ir a la última página';
        if (type === 'next') return 'ir a la página siguiente';
        return 'ir a la página anterior';
      },
      labelDisplayedRows: ({ from, to, count }) => `${from}-${to} de ${count}`,
    },
    localization: {
      ...MRT_Localization_ES,
      noRecordsToDisplay: "No hay datos que mostrar",
      MRT_RowsSelected: '{0} Fila(s) seleccionadas',
    },
    initialState: {
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      density: 'compact',
      columnVisibility: {},
      columnFilters: [],
    },
    enableFilterMatchHighlighting: true,
    enableFilters: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enablePagination: true,
    paginationDisplayMode: 'pages',
    enableColumnDragging: false,
    enablePinning: false,
    muiTableHeadCellProps: {
      sx: {
        ...tableConfig.muiTableHeadCellProps?.sx,
        '& .MuiTableSortLabel-root': {
          fontWeight: 'bold',
        },
        '& .MuiInputBase-input': {
          padding: '2px 5px',
          fontSize: '0.875rem',
        }
      }
    },
    muiTableBodyRowProps: {
      sx: { ...tableConfig.muiTableBodyRowProps?.sx }
    },
    muiSearchTextFieldProps: {
      placeholder: 'Buscar en todas las columnas...',
      size: 'small',
      variant: 'outlined',
      sx: { minWidth: '300px' }
    },
    muiFilterTextFieldProps: {
      placeholder: 'Filtrar...',
      size: 'small',
      variant: 'outlined',
    },
  });

  return (
    <>
      <StyledResolucion.TableContainer>
        <MaterialReactTable table={table} />
      </StyledResolucion.TableContainer>
      <StyledResolucion.ButtonContainer>
        <StyledResolucion.CancelButton
          variant="contained"
          onClick={handleCancelResolucion}
        >
          CANCELAR FORMULARIO
        </StyledResolucion.CancelButton>
        <Button
          disabled={data.length === 0}
          color="primary"
          variant="contained"
          onClick={handleNext}
        >
          SIGUIENTE
        </Button>
      </StyledResolucion.ButtonContainer>
    </>
  );
};

export default PuestosAsociadosTable;